import { ChangeEvent, useState } from "react";
import { Stack, Button, TextField, Container, Typography, Grid, Card } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { toast } from "react-toastify";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

import { useAppDispatch } from "Hooks/useRedux";
import { login } from "Redux/authSlice";

import { API_URL } from "Constants";

interface IInput {
  name: string;
  label: string;
  type?: string;
  grid?: number;
  validation?: boolean;
}

const INPUTS: IInput[] = [
  { name: "name", label: "نام کاربری", validation: true },
  { name: "password", label: "رمز عبور", grid: 6, type: "password" },
  { name: "confirmPassword", label: "تکرار رمز عبور", grid: 6, type: "password" },
  { name: "email", label: "ایمیل", type: "email", validation: true },
];

export default function Register() {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const [step, setStep] = useState<"Register" | "Verify">("Register");
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [isRobot, setIsRobot] = useState<boolean>(true);
  const [verifyCode, setVerifyCode] = useState<{ [key: string]: string }>({});
  const [requestStatus, setRequestStatus] = useState<"Requesting" | "Failed" | "None">("None");

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleRegister = () => {
    if (isRobot) {
      toast.error("من ربات نیستم را تایید کنید");
      // return;
    }
    if (formData.password.length >= 6) {
      if (formData.password === formData.confirmPassword) {
        setRequestStatus("Requesting");
        axios
          .post(`${API_URL}/register`, formData)
          .then((res) => {
            const response = res.data;
            if (response.statusCode === 200) {
              setStep("Verify");
            } else {
              if (response.message === "User exists") {
                toast.error("نام کاربری تکراری است");
                setRequestStatus("Failed");
              } else if (response.message === "Email exists") {
                toast.error("ایمیل تکراری است");
                setRequestStatus("Failed");
              } else {
                setStep("Verify");
              }
            }
          })
          .catch((err) => {
            setRequestStatus("None");
            toast.error(err.response.data.message);
          });
      } else {
        toast.error("رمز عبور و تکرار آن یکی نیست");
      }
    } else {
      toast.error("رمز عبور حداقل 6 رقم باشد");
    }
  };
  const handleVerify = () => {
    let verify = "";
    Object.keys(verifyCode).forEach((key) => (verify += verifyCode[key]));

    axios
      .post(`${API_URL}/active`, { ...formData, code: verify })
      .then((res) => {
        toast.success("فعالسازی با موفقیت انجام شد");
        dispatch(login(res.data.result));
        setRequestStatus("None");
        navigate("/");
      })
      .catch((err) => {
        setRequestStatus("None");
        toast.error(err.response.data.message);
      });
  };
  const handleReCAPTCHA = (value: any) => {
    setIsRobot(false);
  };
  const handleVerifyInput = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (value.length > 0) document.getElementById(`input-${Number(name) + 1}`)?.focus();

    setVerifyCode((prev) => ({ ...prev, [name]: value }));
  };
  const handleOnKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    e = e || window.event;
    let key = e.which || e.keyCode;

    let ctrl = e.ctrlKey ? e.ctrlKey : key === 17 ? true : false;

    if (key == 86 && ctrl) {
      const text = await navigator.clipboard.readText();

      for (let i = 0; i < 4; i++) {
        setVerifyCode((prev) => ({ ...prev, [i]: text[i] }));
      }
    }
  };

  const renderInputs = () => {
    return INPUTS.map((input) => (
      <Grid item xs={12} md={input.grid || 12}>
        <Stack spacing={1}>
          <Typography>{input.label}</Typography>
          <Card>
            <TextField
              type={input.type}
              name={input.name}
              variant="filled"
              fullWidth
              sx={{ "& input": { height: "30px" } }}
              onChange={handleInputChange}
            />
          </Card>
        </Stack>
      </Grid>
    ));
  };

  return (
    <Container maxWidth="xs">
      {step === "Register" ? (
        <>
          <Grid container rowGap={3} columnSpacing={2}>
            {renderInputs()}

            <Grid item xs={12} sx={{ display: "grid", placeItems: "center" }}>
              <ReCAPTCHA sitekey="6LctDTskAAAAALxgxoeUw2XXBwUW_GnWDLVyXEpZ" onChange={handleReCAPTCHA} />
            </Grid>

            <Grid item xs={6}>
              <LoadingButton
                loading={requestStatus === "Requesting"}
                variant="contained"
                color="success"
                fullWidth
                size="large"
                sx={{ py: 1.5 }}
                onClick={handleRegister}
              >
                ساخت حساب
              </LoadingButton>
            </Grid>
            <Grid item xs={6}>
              <Link to="/login">
                <Button variant="contained" color="secondary" fullWidth size="large" sx={{ py: 1.5 }}>
                  ورود به سایت
                </Button>
              </Link>
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          <Typography>ایمیلی حاوی کد فعال سازی به ایمیل شما ارسال شد</Typography>

          <Grid container mt={3} columnSpacing={3} direction="row-reverse">
            {Array.from({ length: 4 }).map((i, index) => (
              <Grid item xs={3}>
                <Card>
                  <TextField
                    id={`input-${index}`}
                    type="text"
                    name={`${index}`}
                    variant="filled"
                    fullWidth
                    sx={{ "& input": { height: "30px", textAlign: "center" } }}
                    inputProps={{ maxLength: 1 }}
                    onChange={handleVerifyInput}
                    onKeyDown={handleOnKeyDown}
                    value={verifyCode[index]}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container mt={5}>
            <Grid item xs={12}>
              <LoadingButton variant="contained" color="success" fullWidth size="large" sx={{ py: 1.5 }} onClick={handleVerify}>
                تایید
              </LoadingButton>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
}
